import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Table from "@/components/elements/Table/Table";
import ResponsiveImage from "@/components/elements/ResponsiveImage/ResponsiveImage";
import SanityImage from "@/components/elements/SanityImage/SanityImage";
import ImageLightbox, { type LightboxItem } from "@/components/elements/ImageLightbox/ImageLightbox";
import { fetchProjectDetail } from "@/lib/queries";
import { imageHqSrc, imageSrc } from "@/lib/imageUrl";
import { fetchLocalProjects, type LocalImageVariants } from "@/lib/localProjects";
import type { SanityImageData } from "@/types/sanity";
import { useSeo } from "@/seo/useSeo";
import { notFoundSeo, projectDetailSeo } from "@/seo/detailSeo";

const FALLBACK_IMAGE = "/project/imgnotavailable.png";
const HERO_IMAGE_WIDTHS = [480, 800, 1200, 1600] as const;
const HERO_IMAGE_SIZES = "(min-width: 800px) 800px, 100vw";
const HERO_IMAGE_STYLE = { maxWidth: "800px", maxHeight: "600px", width: "100%", height: "auto" };
const HERO_IMAGE_CLASS = "my-10 object-contain mx-auto";

/** Compressed image shown instantly in the viewer while the full-quality one loads. */
const VIEWER_BASE_WIDTH = 1200;

function toLightboxItem(project: ProjectView): LightboxItem | null {
  if (project.image) {
    return {
      id: "hero",
      alt: project.title,
      src: imageSrc(project.image, VIEWER_BASE_WIDTH),
      hqSrc: imageHqSrc(project.image),
      width: project.image.dimensions?.width,
      height: project.image.dimensions?.height,
      lqip: project.image.lqip,
    };
  }
  // Local projects only have one file; the viewer still shows it larger.
  // The "image not available" placeholder is not worth enlarging.
  return project.imageUrl && project.imageUrl !== FALLBACK_IMAGE
    ? { id: "hero", alt: project.title, src: project.imageUrl }
    : null;
}

/** What the page renders, regardless of whether the project came from Sanity or JSON. */
interface ProjectView {
  title: string;
  /** Sanity image (CMS projects). */
  image?: SanityImageData | null;
  /** Static image URL (local projects). */
  imageUrl?: string;
  /** Thumbnails for the local image, when they exist. */
  variants?: LocalImageVariants;
  location?: string | null;
  client?: string | null;
  scopeOfWork?: string | null;
  commencement?: string | null;
  finalizationDate?: string | null;
}

const ProjectDetailsPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const [project, setProject] = useState<ProjectView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewerOpen, setViewerOpen] = useState(false);

  useEffect(() => {
    if (!slug) {
      setProject(null);
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    const { signal } = controller;

    const loadProject = async () => {
      setLoading(true);
      setError(null);

      // Sanity wins when a slug exists in both places; the local file is the fallback.
      const [sanityResult, localResult] = await Promise.allSettled([
        fetchProjectDetail(slug, { signal }),
        fetchLocalProjects(signal),
      ]);

      if (signal.aborted) return;

      const sanityProject = sanityResult.status === "fulfilled" ? sanityResult.value : null;
      const localProject =
        localResult.status === "fulfilled"
          ? localResult.value.find((candidate) => candidate.slug === slug) ?? null
          : null;

      const found = sanityProject ?? localProject;

      if (!found && sanityResult.status === "rejected" && localResult.status === "rejected") {
        console.error("Failed to load project:", sanityResult.reason, localResult.reason);
        setError("Failed to fetch project");
      }

      setProject(found);
      setLoading(false);
    };

    loadProject();

    return () => controller.abort();
  }, [slug]);

  useSeo(
    project
      ? projectDetailSeo({
          name: project.title,
          location: project.location,
          client: project.client,
          scopeOfWork: project.scopeOfWork,
          image: project.image
            ? imageSrc(project.image, VIEWER_BASE_WIDTH)
            : project.imageUrl && project.imageUrl !== FALLBACK_IMAGE
              ? project.imageUrl
              : undefined,
          path: `/projects/${slug}`,
        })
      : loading
        ? null
        : notFoundSeo("Project")
  );

  if (loading) return <p className="mt-[5rem] min-h-screen pt-24 text-center text-gray-500">Loading project details...</p>;
  if (error) return <p className="mt-[5rem] min-h-screen pt-24 text-center text-red-600">{error}</p>;
  if (!project) return <p className="mt-[5rem] min-h-screen pt-24 text-center">Project not found.</p>;

  const lightboxItem = toLightboxItem(project);

  const hero = project.image ? (
    <SanityImage
      image={project.image}
      alt={project.title}
      widths={HERO_IMAGE_WIDTHS}
      sizes={HERO_IMAGE_SIZES}
      className={HERO_IMAGE_CLASS}
      style={HERO_IMAGE_STYLE}
      priority
    />
  ) : (
    <ResponsiveImage
      src={project.variants?.src ?? (project.imageUrl || FALLBACK_IMAGE)}
      srcSet={project.variants?.srcSet}
      sizes={project.variants ? HERO_IMAGE_SIZES : undefined}
      width={project.variants?.width}
      height={project.variants?.height}
      lqip={project.variants?.lqip}
      alt={project.title}
      className={HERO_IMAGE_CLASS}
      style={HERO_IMAGE_STYLE}
      priority
    />
  );

  return (
    <div className="max-w-4xl mx-auto mt-10 px-4 py-10">
      {lightboxItem ? (
        <button
          type="button"
          aria-label="View larger image"
          onClick={() => setViewerOpen(true)}
          className="block w-full cursor-zoom-in focus-visible:outline focus-visible:outline-2 focus-visible:outline-custom-hov"
        >
          {hero}
        </button>
      ) : (
        hero
      )}

      {viewerOpen && lightboxItem && (
        <ImageLightbox
          items={[lightboxItem]}
          index={0}
          onClose={() => setViewerOpen(false)}
          onIndexChange={() => {}}
        />
      )}

      <h1 className="text-2xl font-bold my-4 h1">{project.title}</h1>

      <div className="overflow-x-auto w-full">
        <Table
          items={{
            headings: [],
            rows: [
              ["Location", project.location || "N/A"],
              ["Client", project.client || "N/A"],
              ["Commencement Date", project.commencement || "N/A"],
              ["Finalization Date", project.finalizationDate || "N/A"],
              ["Scope of Work", project.scopeOfWork || "N/A"],
            ],
          }}
        />
      </div>
    </div>
  );
};

export default ProjectDetailsPage;
